import queryString from 'query-string';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Optional } from 'chaika-common-types';
import { useUpdatingRef } from '@hooks/useUpdatingRef';

interface QueryFiltersReturn<Q = object> {
    getQueryFilterParams<QP = Q>(): QP;
    updateQueryFilterParams(params: Partial<Q>): void;
    resetQueryFilterParams(): void;
    hasQueryFilterParams(): boolean;
    setFilterGroup(value: string): void;
}

export function useQueryFilters<Q = object>(): QueryFiltersReturn<Q> {
    const [filterGroupName, setFilterGroupName] = useState<Optional<string>>();
    const location = useLocation();
    const navigate = useNavigate();
    const locationRef = useUpdatingRef(location);

    const search = location.search;

    const getQueryFilterParams = useCallback(<QP = Q>(): QP => {
        const query = <Record<string, string>>queryString.parse(search, { arrayFormat: 'bracket' });

        return filterGroupName && query[filterGroupName] ? JSON.parse(query[filterGroupName]) : (query as QP);
    }, [search, filterGroupName]);

    const updateQueryFilterParams = useCallback(
        (params: Partial<Q>) => {
            const query = queryString.parse(locationRef.current.search, { arrayFormat: 'bracket' });
            const newQueryParams = filterGroupName ? { [filterGroupName]: JSON.stringify(params) } : params;

            navigate({ search: queryString.stringify({ ...query, ...newQueryParams }, { arrayFormat: 'bracket' }) });
        },
        [filterGroupName, locationRef, navigate],
    );

    function resetQueryFilterParams(): void {
        if (filterGroupName) {
            const query = queryString.parse(location.search, { arrayFormat: 'bracket' });
            query[filterGroupName] = null;
            navigate({ search: queryString.stringify({ ...query }, { arrayFormat: 'bracket' }) });
        } else {
            navigate({ search: '' });
        }
    }

    function hasQueryFilterParams(): boolean {
        const query = queryString.parse(location.search, { arrayFormat: 'bracket' });
        const { currentPage, ...params } = query;

        if (filterGroupName) {
            return !!query[filterGroupName];
        }

        return Object.keys(params).length !== 0;
    }

    function setFilterGroup(value: string): void {
        setFilterGroupName(value);
    }

    return {
        getQueryFilterParams,
        updateQueryFilterParams,
        resetQueryFilterParams,
        hasQueryFilterParams,
        setFilterGroup,
    };
}
